import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip } from "@amzn/stencil-react-components/chip";
import { Flyout, FlyoutContent } from "@amzn/stencil-react-components/flyout";
import { TokenType, useRecommendedLength } from "@amzn/stencil-react-components/hooks";
import { InputWrapper, RecommendedLengthFooter } from "@amzn/stencil-react-components/input";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { StarRating } from "@amzn/stencil-react-components/star-rating";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { getJobFamilyNameFromMap } from "common/helpers";
import { cacheReview } from "common/helpers/review-cache";
import { getString } from "common/uistringlabels/uiStringUtils";
import { config } from "config";
import { PostFeedbackProps, PostFeedbackResponse } from "hooks/useProductFeedback";
import { CatalogicalDomain } from "lib/services";
import { FeedbackStats } from "lib/services/catalogical-models";
import { CSRFService } from "lib/services/csrf-service";
import React, { FunctionComponent, useEffect, useState } from "react";

import { useMessageBannersBox } from "../MessageBannersBox/hook";

export interface SubmitReviewFormProps {
  titleText: string;
  productName: string;
  taxonomyId: TaxonomyIdItem;
  isOpen: boolean;
  domain: CatalogicalDomain;
  isLoading: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  doPostFeedback: (props: PostFeedbackProps) => Promise<PostFeedbackResponse | undefined>;
  setItemFeedback: (value: React.SetStateAction<FeedbackStats | undefined>) => void;
  previousRating: number;
  setPreviousRating: (value: React.SetStateAction<number>) => void;
  onReviewSubmitted?: () => any;
}

const CATEGORIES = ["Features", "Accuracy", "Performance", "Integration", "Usability", "Others"];
const successMessage = getString("submitReview.successMessage");
const failMessage = getString("submitReview.failMessage");

const REVIEW_CONTENT_WORD_LIMIT = 150;
const REVIEW_TITLE_WORD_LIMIT = 20;

export const SubmitReviewForm: FunctionComponent<SubmitReviewFormProps> = ({
  titleText,
  productName,
  taxonomyId,
  isOpen,
  setIsOpen,
  isLoading,
  doPostFeedback,
  setItemFeedback,
  previousRating,
  setPreviousRating,
  onReviewSubmitted,
}) => {
  const csrfService = new CSRFService();
  const [enableConfirmButton, setEnableConfirmButton] = useState(false);
  const [reviewContentError, setReviewContentError] = useState(false);
  const [reviewTitleError, setReviewTitleError] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [rating, setRating] = useState<number>(previousRating);
  const [reviewTitle, setReviewTitle] = useState<string>("");
  const [reviewContent, setReviewContent] = useState<string>("");
  const {
    footerText: reviewContentFooterText,
    progress: reviewContentProgress,
    count: reviewContentCount,
  } = useRecommendedLength({
    value: reviewContent,
    tokenType: TokenType.Words,
    recommendedLength: REVIEW_CONTENT_WORD_LIMIT,
    getExceededText: ({ tokenCount, recommendedLength }) =>
      getString("submitReview.exceedString", { recommendedLength }),
  });

  const {
    footerText: reviewTitleFooterText,
    progress: reviewTitleProgress,
    count: reviewTitleCount,
  } = useRecommendedLength({
    value: reviewTitle,
    tokenType: TokenType.Words,
    recommendedLength: REVIEW_TITLE_WORD_LIMIT,
    getExceededText: ({ tokenCount, recommendedLength }) =>
      getString("submitReview.exceedString", { recommendedLength }),
  });

  const { addErrorBanner, addMessageBanner } = useMessageBannersBox();

  // Review title does not exceed limit
  useEffect(() => {
    if (reviewTitleCount > REVIEW_TITLE_WORD_LIMIT) {
      setReviewTitleError(true);
    } else {
      setReviewTitleError(false);
    }
  }, [reviewTitleCount]);

  // Review content does not exceed limit
  useEffect(() => {
    if (reviewContentCount > REVIEW_CONTENT_WORD_LIMIT) {
      setReviewContentError(true);
    } else {
      setReviewContentError(false);
    }
  }, [reviewContentCount]);

  // Enable submit once all values are filled
  useEffect(() => {
    if (isFormValid()) {
      setEnableConfirmButton(true);
    } else {
      setEnableConfirmButton(false);
    }
  }, [selectedCategories, reviewTitle, reviewContent, reviewTitleError, reviewContentError, rating]);

  const close = () => {
    setIsOpen(false);
    trackUxmChange("click", "cancel-submit-review");
  };

  const clearFields = () => {
    setSelectedCategories([]);
    setReviewTitle("");
    setReviewContent("");
  };

  const handleSubmit = async () => {
    if (isFormValid()) {
      const response = await doPostFeedback({
        taxonomyId,
        csrfToken: await csrfService.getToken(),
        feedback: {
          rating,
          content: reviewContent,
          title: reviewTitle,
          categories: selectedCategories.map((index) => CATEGORIES[index]),
          jobFamily: await getJobFamilyNameFromMap(),
        },
      });
      if (response) {
        addMessageBanner(successMessage, MessageBannerType.Success);
        setItemFeedback(response.itemStats);
        setPreviousRating(rating);
        cacheReview(taxonomyId.toString(), response.feedback);
        clearFields();
        window.scrollTo({ top: 0, behavior: "smooth" });
        if (onReviewSubmitted) onReviewSubmitted();
        close();
      } else {
        addErrorBanner(failMessage);
      }
    }
  };

  const isFormValid = () => {
    return (
      selectedCategories.length > 0 &&
      reviewTitle.length > 0 &&
      reviewContent.length > 0 &&
      !reviewTitleError &&
      !reviewContentError &&
      rating > 0
    );
  };

  const trackUxmChange = (option: any, touchpoint: string) => {
    const trackerId = config.analytics.uxmAppId;
    const event = {
      option,
    };

    trackCustomEvent(event, touchpoint, trackerId);
  };

  const buttonCancel = (
    <Button
      onClick={close}
      variant={ButtonVariant.Secondary}
      data-test-id="submit-review-button-cancel"
      touchpoint="cancel-submit-review"
    >
      {getString("submitReview.cancelButton")}
    </Button>
  );

  const buttonSubmit = (
    <Button
      onClick={handleSubmit}
      variant={ButtonVariant.Primary}
      data-test-id="submit-review-button-submit"
      disabled={!enableConfirmButton || isLoading}
      touchpoint="post-submit-review"
      customData={{ taxonomyId }}
    >
      {getString("submitReview.submitButton")}
    </Button>
  );

  const renderCategories = () => {
    return CATEGORIES.map((category, index) => {
      return (
        <Chip
          key={`category-chip-${index}`}
          data-test-id={`${category}-chip`}
          isSelected={selectedCategories.includes(index)}
          onClick={() => {
            if (selectedCategories.includes(index)) {
              trackUxmChange(category, "remove-select-review-categories");
              setSelectedCategories(selectedCategories.filter((chip) => chip !== index));
            } else {
              trackUxmChange(category, "add-select-review-categories");
              setSelectedCategories([...selectedCategories, index]);
            }
          }}
        >
          {category}
        </Chip>
      );
    });
  };

  const renderRating = () => {
    return (
      <Col>
        <Label style={{ marginBottom: "8px" }}>{getString("submitReview.yourRatingLabel")}</Label>
        <StarRating
          name="submit-review-star-rating"
          data-test-id="submit-review-star-rating"
          value={rating}
          onChange={(e) => {
            trackUxmChange(e, "select-review-star-rating");
            setRating(e);
          }}
        />
      </Col>
    );
  };

  const renderReviewTitle = () => {
    return (
      <InputWrapper
        id="submit-review-title-input"
        dataTestId="submit-review-title-input"
        labelText={getString("submitReview.reviewTitleLabel")}
        error={reviewTitleError}
        footer={(footerProps) =>
          reviewTitleError && (
            <RecommendedLengthFooter progress={reviewTitleProgress} {...footerProps}>
              {reviewTitleFooterText}
            </RecommendedLengthFooter>
          )
        }
      >
        {(inputProps) => (
          <TextArea
            {...inputProps}
            name="submit-review-title-input"
            data-test-id="submit-review-title-input"
            placeholder={getString("submitReview.reviewTitlePlaceholder")}
            value={reviewTitle}
            onChange={(e) => setReviewTitle(e.target.value)}
            disabled={isLoading}
            touchpoint="review-title-input"
          />
        )}
      </InputWrapper>
    );
  };

  const renderReviewContent = () => {
    return (
      <InputWrapper
        id="submit-review-content-input"
        dataTestId="submit-review-content-input"
        labelText={getString("submitReview.reviewContentLabel")}
        error={reviewContentError}
        footer={(footerProps) => (
          <RecommendedLengthFooter progress={reviewContentProgress} {...footerProps}>
            {reviewContentFooterText}
          </RecommendedLengthFooter>
        )}
      >
        {(inputProps) => (
          <TextArea
            {...inputProps}
            name="submit-review-content-input"
            data-test-id="submit-review-content-input"
            placeholder={getString("submitReview.reviewContentPlaceholder")}
            resize="vertical"
            height={"314px"}
            value={reviewContent}
            onChange={(e) => setReviewContent(e.target.value)}
            disabled={isLoading}
            touchpoint="review-content-input"
          />
        )}
      </InputWrapper>
    );
  };

  const renderReviewCategories = () => {
    return (
      <Col>
        <Label style={{ marginBottom: "8px" }}>{getString("submitReview.reviewCategoriesLabel")}</Label>
        <Row gridGap={"12px"} flexWrap="wrap" rowGap={"16px"} dataTestId="submit-review-categories">
          {renderCategories()}
        </Row>
      </Col>
    );
  };

  return (
    <Flyout isOpen={isOpen} close={close} dataTestId="submit-review-form">
      <FlyoutContent onCloseButtonClick={close} titleText={titleText} buttons={[buttonCancel, buttonSubmit]}>
        <Col width={"544px"} rowGap={"20px"}>
          <Text fontWeight={"bold"}>{productName}</Text>
          {renderRating()}
          {renderReviewTitle()}
          {renderReviewContent()}
          {renderReviewCategories()}
        </Col>
      </FlyoutContent>
    </Flyout>
  );
};
